import { useQuery } from '@tanstack/react-query'
import { dayRange, type Repeats } from '../../shared/calendar'
import { api } from './api'
import { EVERYONE } from './families'
import type { Place } from './spaces'

// The calendar's days, as the server tells them (worker/calendar.ts): birthdays and remembrance days from the tree's
// full dates, and On this day from dated photos and moments. Everyone's (all your circles together) says which
// circles each day comes from. Dates people add themselves fall in among them (worker/calendarDates.ts).
type Person = { id: string; name: string; pet: boolean; portrait: string | null }
export type Circle = { id: string; name: string }
export type CalendarEntry = { on: string; circles?: Circle[] } & (
  | { kind: 'birthday' | 'remembrance' | 'remembered-birthday'; person: Person; years: number; leapDay: boolean }
  | { kind: 'on-this-day'; photos: number; moments: number; years: number[] }
  | { kind: 'date'; date: AddedDate }
)
// One day of a date someone added: which of its days this is when it runs over several.
export type AddedDate = {
  id: string
  title: string
  startsAt: string | null
  endsAt: string | null
  dayOf: number
  days: number
  repeats: Repeats
  onPrints: boolean
  mine: boolean
  by: string
}
// A date whole, as it opens.
export type CalendarDate = {
  id: string
  familyId: string
  title: string
  startsOn: string
  endsOn: string | null
  startsAt: string | null
  endsAt: string | null
  repeats: Repeats
  repeatsUntil: string | null
  note: string
  onPrints: boolean
  mine: boolean
  author: { id: string; name: string }
  everyone: boolean
  sharedWith: Place[]
}
export type Missing = { id: string; name: string; born: string | null; portrait: string | null }
export type CircleCalendar = { from: string; days: number; entries: CalendarEntry[]; missing: Missing[] }
export type EveryoneCalendar = { from: string; days: number; circles: Circle[]; entries: CalendarEntry[] }

const params = (values: Record<string, string | number | undefined>) =>
  new URLSearchParams(Object.entries(values).flatMap(([k, v]) => (v === undefined ? [] : [[k, String(v)]]))).toString()

export const useCircleCalendar = (
  familyId: string,
  spaceId: string | undefined,
  from: string,
  days: number,
  enabled = true,
) =>
  useQuery({
    queryKey: ['calendar', familyId, spaceId ?? 'all', from, days],
    // Everyone's calendar is every circle's together, with dates your connections share with you (useEveryoneCalendar).
    queryFn: async () =>
      familyId === EVERYONE
        ? { ...(await api<EveryoneCalendar>(`/calendar?${params({ from, days })}`)), missing: [] }
        : api<CircleCalendar>(`/families/${familyId}/calendar?${params({ from, days, space: spaceId })}`),
    enabled,
  })

export const useCalendarDate = (id: string | null) =>
  useQuery({
    queryKey: ['calendar-date', id],
    queryFn: () => api<CalendarDate>(`/calendar/dates/${id}`),
    enabled: !!id,
  })

export const useEveryoneCalendar = (from: string, days: number, enabled = true) =>
  useQuery({
    queryKey: ['calendar', 'everyone', from, days],
    queryFn: () => api<EveryoneCalendar>(`/calendar?${params({ from, days })}`),
    enabled,
  })

// A month: from its first day, all its days.
export function monthSpan(year: number, month: number) {
  const days = new Date(Date.UTC(year, month, 0)).getUTCDate()
  return { from: `${year}-${String(month).padStart(2, '0')}-01`, days }
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`

// "Abuela" for a line about her. Years are counted from the day itself, so they're real.
export function entryTitle(entry: CalendarEntry) {
  switch (entry.kind) {
    case 'birthday':
    case 'remembered-birthday':
      return `${entry.person.name}’s birthday`
    case 'remembrance':
      return `Remembering ${entry.person.name}`
    case 'on-this-day':
      return 'On this day'
    case 'date':
      return entry.date.title
  }
}

// "9:30 AM", from the wall-clock "09:30".
export function clockTime(time: string) {
  const [h, m] = time.split(':').map(Number) as [number, number]
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`
}

export const REPEAT_LABELS: Record<Repeats, string> = {
  none: 'Doesn’t repeat',
  daily: 'Every day',
  weekly: 'Every week',
  monthly: 'Every month',
  yearly: 'Every year',
}

// When on its day: all day, or its times. On a day in the middle of several, it's on all day.
export function dateTimes(date: Pick<AddedDate, 'startsAt' | 'endsAt' | 'dayOf' | 'days'>) {
  if (!date.startsAt) return 'All day'
  if (date.days > 1) {
    if (date.dayOf === 1) return `From ${clockTime(date.startsAt)}`
    if (date.dayOf === date.days && date.endsAt) return `Until ${clockTime(date.endsAt)}`
    return 'All day'
  }
  return date.endsAt ? `${clockTime(date.startsAt)} – ${clockTime(date.endsAt)}` : clockTime(date.startsAt)
}

export function entryDetail(entry: CalendarEntry) {
  switch (entry.kind) {
    case 'birthday':
      return `Turns ${entry.years}${entry.leapDay ? ' · born on February 29' : ''}`
    case 'remembered-birthday':
      return `Born in ${Number(entry.on.slice(0, 4)) - entry.years}`
    case 'remembrance':
      return `${plural(entry.years, 'year')} since ${Number(entry.on.slice(0, 4)) - entry.years}`
    case 'on-this-day': {
      const kept = [
        entry.photos ? plural(entry.photos, 'photo') : '',
        entry.moments ? plural(entry.moments, 'moment') : '',
      ].filter(Boolean)
      const years = entry.years.length > 3 ? `${entry.years.slice(0, 3).join(', ')} and earlier` : listed(entry.years)
      return `${kept.join(' and ')} from ${years}`
    }
    case 'date': {
      const { date } = entry
      return [
        dateTimes(date),
        date.days > 1 ? `Day ${date.dayOf} of ${date.days}` : '',
        date.repeats === 'none' ? '' : REPEAT_LABELS[date.repeats],
        date.mine ? '' : `Added by ${date.by}`,
      ]
        .filter(Boolean)
        .join(' · ')
    }
  }
}

const listed = (years: number[]) =>
  years.length < 2 ? String(years[0] ?? '') : `${years.slice(0, -1).join(', ')} and ${years.at(-1)}`

// Where a day leads: the person's page (their memorial page, for someone who has passed), the day told as a story, or
// an added date opened on the calendar (this one, from Everyone).
export function entryLink(entry: CalendarEntry, familyId: string | undefined) {
  if (entry.kind === 'date') {
    const opened = `?month=${entry.on.slice(0, 7)}&date=${entry.date.id}`
    return familyId ? `/circle/${familyId}/calendar${opened}` : opened
  }
  if (entry.kind !== 'on-this-day') return `/person/${entry.person.id}`
  const circle = familyId ?? entry.circles?.[0]?.id
  return circle ? `/circle/${circle}/on-this-day/story?day=${entry.on}` : null
}

// Days that are coming up: about someone (a birthday, a remembrance day), or added. On this day has its own place on
// Home. A date running over several days comes up on its first.
export const comingUp = (entry: CalendarEntry) =>
  entry.kind !== 'on-this-day' && (entry.kind !== 'date' || entry.date.dayOf === 1)

// "Today", "Tomorrow", "Saturday" within the week, and "Sat, Oct 24" after that, from the viewer's today.
export function whenLabel(on: string, today: string) {
  const days = Math.round((Date.parse(`${on}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 86_400_000)
  const date = new Date(`${on}T00:00:00Z`)
  if (days === 0) return 'Today'
  if (days === 1) return 'Tomorrow'
  if (days > 1 && days < 7) return date.toLocaleDateString('en-US', { weekday: 'long', timeZone: 'UTC' })
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' })
}

// The days of a range, each with what falls on it.
export function byDay(entries: CalendarEntry[], from: string, days: number) {
  const map = new Map<string, CalendarEntry[]>(dayRange(from, days).map((day) => [day, []]))
  for (const entry of entries) map.get(entry.on)?.push(entry)
  return map
}
